import { useEffect, useState } from 'react';
import type { Icon } from '@phosphor-icons/react';
import clsx from 'clsx';

// SVG ring whose stroke fills proportional to spent/planned, with an
// optional red overlay arc showing the over-budget portion. Used both
// inline in budget rows (44px, 2.5 stroke) and as the focal element of
// dashboard tiles (larger, thicker). The track always renders so the
// container's geometry doesn't shift between has-actual and no-actual
// states; the colored arc is only drawn when there's spending to show.
//
// When over budget, two signals stack:
//   1. The ring splits into two segments on the same circle: a red arc
//      from 12-o'clock sized to the overage percentage (capped at 100%
//      so a 5x-budget category doesn't lap multiple times), then the
//      colored arc filling the rest of the ring. A small angular gap
//      separates the segments at each seam — same look as the donut
//      chart's paddingAngle.
//   2. The area INSIDE the ring is tinted red-hard so the over-budget
//      state reads at a glance — flags *that* you're over even when the
//      overage arc is small.
export function CategoryProgressRing({
  emoji,
  Icon,
  spentPct,
  overPct,
  color,
  showRing,
  size,
  strokeWidth,
}: {
  emoji: string | null;
  Icon: Icon | null;
  spentPct: number;
  overPct: number;
  color: string;
  showRing: boolean;
  // Optional size override. Defaults: 44 with ring, 32 without (matches the
  // budget-row layout). Tiles pass a larger explicit size.
  size?: number;
  strokeWidth?: number;
}) {
  const SIZE = size ?? (showRing ? 44 : 32);
  const STROKE = strokeWidth ?? 2.5;
  const RADIUS = (SIZE - STROKE) / 2;
  // Glyph sizing tracks the container: small rings get text-lg, medium
  // ~text-2xl, larger ~text-3xl. The icon fallback uses ~45% of the
  // container width as its glyph size so it visually balances the emoji.
  const emojiSizeClass =
    size != null
      ? size >= 96
        ? 'text-4xl'
        : size >= 72
          ? 'text-3xl'
          : size >= 56
            ? 'text-2xl'
            : 'text-lg'
      : showRing
        ? 'text-lg'
        : 'text-2xl';
  const iconSize = size != null ? Math.floor(size * 0.45) : showRing ? 20 : 24;
  // Tinted track so the unfilled portion still reads as part of the same
  // metric — empty rings carry color identity even at 0% fill.
  const trackColor = `color-mix(in srgb, ${color} 22%, transparent)`;
  // Animate the arc on mount: commit at 0, then bump to the real value next
  // tick so the CSS transition handles the swoop. Mirrors ProgressFill.
  const [arc, setArc] = useState(0);
  const [overArc, setOverArc] = useState(0);
  useEffect(() => {
    if (showRing) {
      setArc(spentPct);
      setOverArc(overPct);
    } else {
      setArc(0);
      setOverArc(0);
    }
  }, [showRing, spentPct, overPct]);

  // Inner fill radius for the over-budget tint: stops at the inner edge of
  // the stroke so the ring itself remains crisp against the red-soft hole.
  const INNER_RADIUS = Math.max(0, RADIUS - STROKE / 2);
  // Visible angular gap (in pathLength units, where 100 = full ring) used
  // between the red overage segment and the colored segment at each seam.
  // ~3 units ≈ 10.8° — slightly wider than the donut chart's paddingAngle
  // since each ring only has two segments to separate.
  const SEAM_GAP = 3;
  const halfGap = SEAM_GAP / 2;
  // Per-segment visible lengths after carving out the two seam gaps. Clamped
  // at 0 so a tiny overage doesn't paint a negative-length dash.
  const redLength = Math.max(0, overArc - SEAM_GAP);
  const colorLength = Math.max(0, 100 - overArc - SEAM_GAP);

  return (
    <div className="relative flex-none" style={{ width: SIZE, height: SIZE }}>
      {showRing && (
        <svg
          aria-hidden
          className="absolute inset-0 -rotate-90"
          viewBox={`0 0 ${SIZE} ${SIZE}`}
        >
          {overPct > 0 && (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={INNER_RADIUS}
              fill="var(--berg-red-hard)"
            />
          )}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={trackColor}
            strokeWidth={STROKE}
          />
          {overPct > 0 ? (
            <>
              {/* Red overage segment: starts at 12 o'clock offset by half
                  the seam gap, sweeps clockwise for (overArc - gap) units. */}
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="var(--berg-red)"
                strokeWidth={STROKE}
                strokeDasharray={`${redLength} 100`}
                pathLength={100}
                strokeLinecap="round"
                transform={`rotate(${halfGap * 3.6} ${SIZE / 2} ${SIZE / 2})`}
                style={{
                  transition: 'stroke-dasharray 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
              {/* Colored segment: rotated past the overage + gap so it
                  starts after the seam, sweeps the remainder of the ring. */}
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={color}
                strokeWidth={STROKE}
                strokeDasharray={`${colorLength} 100`}
                pathLength={100}
                strokeLinecap="round"
                transform={`rotate(${(overArc + halfGap) * 3.6} ${SIZE / 2} ${SIZE / 2})`}
                style={{
                  transition: 'stroke-dasharray 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            </>
          ) : (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={color}
              strokeWidth={STROKE}
              strokeDasharray={`${arc} 100`}
              pathLength={100}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 700ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            />
          )}
        </svg>
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        {emoji ? (
          <span aria-hidden className={clsx(emojiSizeClass, 'leading-none')}>
            {emoji}
          </span>
        ) : Icon ? (
          <Icon size={iconSize} weight="duotone" className="text-fg-secondary" />
        ) : null}
      </div>
    </div>
  );
}
